import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Home as HomeIcon, Key, MapPin, TrendingUp, Smartphone, Search, Heart, MessageSquare, Filter, Sparkles, CheckCircle2, ExternalLink, Clock, DollarSign, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { useSiteSettings, pickText } from "@/lib/siteSettings";

const HERO_IMG =
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const services = [
  {
    icon: HomeIcon,
    title: "Sellers",
    blurb: "Honest pricing, beautiful marketing, and a calm hand through every step.",
    points: ["Full-service listing strategy", "Professional photo + video", "Pre-list home prep guidance"],
  },
  {
    icon: Key,
    title: "Buyers",
    blurb: "Real advocacy from search through closing. No pressure, no shortcuts.",
    points: ["Neighborhood guidance", "Inspection + negotiation expertise", "First-time buyer education"],
  },
  {
    icon: TrendingUp,
    title: "Investors",
    blurb: "Long-term thinking for clients who are building real estate into their financial plan.",
    points: ["Rental + cash-flow analysis", "BRRRR + small multifamily", "Network of trusted lenders & GC's"],
  },
];

const KW_SEARCH_URL_DEFAULT = "https://tammyrichard.kw.com/";

function formatCurrency(n) {
  if (n == null) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatMonth(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function RealEstate() {
  return (
    <div data-testid="real-estate-page">
      {/* Home Value — at the very top */}
      <HomeValueSection />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Warm home interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 w-full">
          <span className="inline-block text-[11px] uppercase tracking-[0.32em] text-white/85 mb-6">
            Real Estate · Keller Williams Maine
          </span>
          <h1 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-3xl">
            A grounded agent
            <br />
            for a <span className="italic text-[#93C5FD]" style={{ textShadow: "0 2px 24px rgba(147,197,253,0.35)" }}>big decision.</span>
          </h1>
          <p className="text-white/85 text-lg mt-6 max-w-xl leading-relaxed">
            Twenty-seven years guiding Maine families through buying, selling,
            and investing — without the hustle, hype, or pressure tactics.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            ["27 yrs", "Associate Broker"],
            ["KW Maine", "Brokerage"],
            ["6+", "Certifications"],
            ["Greater Portland", "Service area"],
          ].map(([n, l]) => (
            <div key={l}>
              <p className="font-serif text-3xl md:text-4xl">{n}</p>
              <p className="text-xs uppercase tracking-[0.22em] text-foreground/60 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Who Tammy works with.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/75 leading-relaxed self-end">
            Whether it's your first home or your tenth investment, the
            standard is the same: clear thinking and steady representation.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div
              key={s.title}
              className="bg-card border border-border rounded-2xl p-8 card-lift"
              data-testid={`re-service-${i}`}
            >
              <s.icon className="text-primary" size={28} strokeWidth={1.5} />
              <h3 className="font-serif text-3xl mt-5">{s.title}</h3>
              <p className="text-sm text-foreground/70 mt-3 leading-relaxed">{s.blurb}</p>
              <ul className="mt-6 space-y-2 text-sm text-foreground/80">
                {s.points.map((p) => (
                  <li key={p}>— {p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Search Maine real estate + Neighborhood explorer */}
      <SearchAndNeighborhoodSection />

      {/* Download the App */}
      <section className="bg-[#0F1E33] text-[#F5F8FB] py-24" data-testid="download-app-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#93C5FD] mb-4">
              <Smartphone size={14} /> Download the App
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Search, save, collaborate
              <br />
              — <span className="italic text-[#93C5FD]">in your pocket.</span>
            </h2>
            <p className="mt-6 text-lg opacity-85 leading-relaxed max-w-xl">
              Curious about what's trending in your neighborhood? Ready to make
              a move? Download Tammy's KW app for real estate insights, on
              demand — tailored to her market.
            </p>

            <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
              <div>
                <p className="font-serif text-3xl">1M+</p>
                <p className="text-xs uppercase tracking-[0.18em] opacity-60 mt-1">Downloads</p>
              </div>
              <div>
                <p className="font-serif text-3xl">40+</p>
                <p className="text-xs uppercase tracking-[0.18em] opacity-60 mt-1">Filters</p>
              </div>
              <div>
                <p className="font-serif text-3xl">60+</p>
                <p className="text-xs uppercase tracking-[0.18em] opacity-60 mt-1">Countries</p>
              </div>
            </div>

            <a
              href="https://tammyrichard.kw.com/download-the-app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-app-cta"
            >
              <Button className="mt-10 rounded-full bg-[#93C5FD] hover:bg-[#BAE6FD] text-[#0F1E33] btn-lift h-12 px-8 text-base">
                Get the app <ArrowRight className="ml-2" size={18} />
              </Button>
            </a>
            <p className="text-xs opacity-55 mt-3">
              Scan the QR code on the landing page, or open the link on your phone.
            </p>
          </div>

          {/* Feature grid */}
          <div className="md:col-span-5 grid grid-cols-2 gap-4">
            {[
              { icon: Search, t: "Smart Search", d: "Custom filters that matter to you." },
              { icon: Heart, t: "Save Favorites", d: "Track listings & price changes." },
              { icon: Filter, t: "Virtual Tours", d: "Walk through homes from your couch." },
              { icon: MessageSquare, t: "Connect Direct", d: "Message Tammy from the app." },
              { icon: Sparkles, t: "Home Value", d: "Check your home's estimated value." },
              { icon: TrendingUp, t: "Market Insights", d: "Real-time trends in your area." },
            ].map((f) => (
              <div key={f.t} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <f.icon className="text-[#93C5FD]" size={20} strokeWidth={1.6} />
                <p className="font-serif text-base mt-3 leading-tight">{f.t}</p>
                <p className="text-xs opacity-70 mt-1.5 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* User quote */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 mt-16 pt-12 border-t border-white/10">
          <p className="font-serif italic text-xl md:text-2xl leading-snug opacity-90 text-center">
            "I love this app because it updates on trends in my neighborhood
            and helps me see how much my home is valued. The search engine is
            better than most. I recommend it to anyone interested in selling
            or buying real estate."
          </p>
          <p className="text-xs uppercase tracking-[0.22em] opacity-55 mt-6 text-center">
            — KW App User
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h2 className="font-serif text-4xl md:text-5xl leading-tight">
          Thinking about a move?
        </h2>
        <p className="text-lg text-foreground/75 mt-6">
          Let's grab a coffee or a call. No pressure to do anything — just
          good information.
        </p>
        <div className="flex flex-wrap gap-3 justify-center mt-10">
          <Link to="/booking" data-testid="re-cta-book">
            <Button className="rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
              Book a Call <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <Link to="/contact" data-testid="re-cta-contact">
            <Button variant="outline" className="rounded-full h-12 px-8 border-border">
              Send a message
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ---------- Home Value section (top of Real Estate page) ---------- */

const HOMEVALUE_IMG =
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function HomeValueSection() {
  const [form, setForm] = useState({ address: "", name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.address || !form.email || !form.name) {
      toast.error("Please share your name, email, and address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Home Value Request",
        inquiry_type: "general",
        message: `Property address: ${form.address}\n\nRequesting a home valuation.`,
      });
      toast.success("Request received! Tammy will be in touch shortly.");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-value-section"
    >
      <div className="absolute inset-0">
        <img src={HOMEVALUE_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1E33]/95 via-[#0F1E33]/85 to-[#0F1E33]/55" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 text-white">
          <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#93C5FD] mb-5">
            Free home valuation
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            What's your home
            <br />
            <span className="italic text-[#93C5FD]">worth today?</span>
          </h2>
          <p className="mt-6 text-base md:text-lg opacity-85 leading-relaxed max-w-md">
            Get a custom market valuation from Tammy — not an algorithm.
            Pulled from current comparable sales in your neighborhood,
            reviewed personally, and delivered to your inbox.
          </p>
          <ul className="mt-6 space-y-2 text-sm opacity-90">
            {[
              "Hand-reviewed by a 27-year Associate Broker",
              "Comparable-sale analysis, not just an estimate",
              "No obligation — and no pressure",
            ].map((t) => (
              <li key={t} className="flex gap-2 items-start">
                <CheckCircle2 className="text-[#93C5FD] shrink-0 mt-0.5" size={15} strokeWidth={1.7} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {done ? (
            <div className="bg-white rounded-2xl p-10 text-center" data-testid="home-value-success">
              <CheckCircle2 className="mx-auto text-primary" size={48} strokeWidth={1.4} />
              <h3 className="font-serif text-3xl mt-5">Request received.</h3>
              <p className="text-foreground/70 mt-3 leading-relaxed">
                Tammy will personally review and email you a home valuation
                within 1–2 business days.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="bg-white rounded-2xl p-7 md:p-8 shadow-2xl"
              data-testid="home-value-form"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-primary font-medium">
                Get my home value
              </p>
              <p className="font-serif text-2xl text-foreground mt-1 leading-tight">
                Tell us about your home.
              </p>

              <div className="mt-5 space-y-4">
                <FieldBlock label="Property address *">
                  <Input value={form.address} onChange={update("address")} placeholder="123 Main St, Portland, ME"
                    className="rounded-xl h-11" data-testid="home-value-address" required />
                </FieldBlock>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FieldBlock label="Your name *">
                    <Input value={form.name} onChange={update("name")} className="rounded-xl h-11"
                      data-testid="home-value-name" placeholder="Jane Cooper" required />
                  </FieldBlock>
                  <FieldBlock label="Phone">
                    <Input value={form.phone} onChange={update("phone")} className="rounded-xl h-11"
                      data-testid="home-value-phone" placeholder="(207) 555-0000" />
                  </FieldBlock>
                </div>
                <FieldBlock label="Email *">
                  <Input type="email" value={form.email} onChange={update("email")} className="rounded-xl h-11"
                    data-testid="home-value-email" placeholder="jane@example.com" required />
                </FieldBlock>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="home-value-submit"
                className="w-full mt-5 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 text-base"
              >
                {submitting ? "Sending..." : "Send my home value"}
                {!submitting && <ArrowRight className="ml-2" size={16} />}
              </Button>
              <p className="text-[10px] text-center text-foreground/55 mt-3">
                Your info stays private and is only used to send your valuation.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.16em] text-foreground/65 font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/* ---------- Search + Neighborhood Explorer (live RentCast data) ---------- */

function SearchAndNeighborhoodSection() {
  const { settings } = useSiteSettings();
  const KW_SEARCH_URL = pickText(settings, "links.kwMlsUrl", KW_SEARCH_URL_DEFAULT);
  const [query, setQuery] = useState("");
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Load the town catalog once
  useEffect(() => {
    let cancelled = false;
    api
      .get("/market/neighborhoods")
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.neighborhoods || [];
        setNeighborhoods(list);
        if (list.length) setSelectedSlug(list[0].slug);
      })
      .catch(() => {
        if (!cancelled) setNeighborhoods([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load snapshot whenever the selected slug changes
  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    setSnapshotLoading(true);
    api
      .get(`/market/snapshot/${selectedSlug}`)
      .then((res) => {
        if (!cancelled) setSnapshot(res.data);
      })
      .catch(() => {
        if (!cancelled) setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled) setSnapshotLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  const selectedName =
    snapshot?.name ||
    neighborhoods.find((n) => n.slug === selectedSlug)?.name ||
    "";

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    const url = q
      ? `${KW_SEARCH_URL}?searchTerm=${encodeURIComponent(q)}`
      : KW_SEARCH_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const yoy = snapshot?.yoy;
  const yoyPositive = yoy != null && yoy >= 0;
  const snapshotMonth = formatMonth(snapshot?.fetchedAt);
  const sourceLabel = snapshot?.source === "live"
    ? `Live · RentCast${snapshotMonth ? ` · ${snapshotMonth}` : ""}`
    : snapshot?.source === "cached"
    ? `Cached · RentCast${snapshotMonth ? ` · ${snapshotMonth}` : ""}`
    : snapshot?.source === "override"
    ? "Manual snapshot · updated by Tammy"
    : "Snapshot · Greater Portland MLS";

  return (
    <section className="bg-muted/40 py-24" data-testid="market-explorer-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <MapPin size={14} /> Greater Portland · Areas served
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Explore the neighborhoods.
            </h2>
            <p className="text-foreground/80 mt-6 leading-relaxed text-lg max-w-2xl">
              Pick a town to see what's happening on the ground — median
              prices, days on market, and where it's headed. Want to see live
              listings? Search Tammy's MLS portal below.
            </p>
          </div>
        </div>

        {/* Search bar — points to KW MLS */}
        <form
          onSubmit={onSearch}
          className="bg-card border border-border rounded-2xl p-6 md:p-7 mb-12 shadow-sm"
          data-testid="kw-search-form"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-[0.18em] text-foreground/65 font-medium">
                Search Maine real estate
              </Label>
              <div className="relative mt-2">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none"
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Town, ZIP, address, or MLS #"
                  className="pl-11 rounded-full h-12 text-base"
                  data-testid="kw-search-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="kw-search-submit"
              className="md:self-end rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-7 text-base"
            >
              Search MLS <ExternalLink className="ml-2" size={16} />
            </Button>
          </div>
          <p className="text-xs text-foreground/55 mt-3">
            Opens Tammy's KW Maine search portal in a new tab.
          </p>
        </form>

        {/* Neighborhood dropdown + stats */}
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5">
            <div
              className="bg-card border border-border rounded-2xl p-7"
              data-testid="neighborhood-picker"
            >
              <p className="text-xs uppercase tracking-[0.22em] text-foreground/60">
                Neighborhood explorer
              </p>
              <p className="font-serif text-2xl mt-2 leading-tight">
                Pick a town to see the latest market snapshot.
              </p>

              <div className="mt-6">
                <Label className="text-xs uppercase tracking-[0.16em] text-foreground/65 font-medium">
                  Town
                </Label>
                <Select
                  value={selectedSlug}
                  onValueChange={setSelectedSlug}
                >
                  <SelectTrigger
                    className="mt-2 rounded-full h-12 text-base"
                    data-testid="neighborhood-select-trigger"
                  >
                    <SelectValue placeholder="Choose a town…" />
                  </SelectTrigger>
                  <SelectContent
                    className="max-h-72"
                    data-testid="neighborhood-select-content"
                  >
                    {neighborhoods.map((n) => (
                      <SelectItem
                        key={n.slug}
                        value={n.slug}
                        data-testid={`neighborhood-option-${n.slug}`}
                      >
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <p className="text-[10px] uppercase tracking-[0.22em] text-foreground/50 mt-6">
                {sourceLabel}
              </p>
              <p className="text-xs text-foreground/60 mt-2 leading-relaxed">
                Single-family sale stats pulled from RentCast and refreshed
                daily. Reach out for a hyper-local breakdown of your block.
              </p>
            </div>
          </div>

          <div
            className="md:col-span-7 space-y-4"
            data-testid={`neighborhood-stats-${selectedSlug}`}
          >
            {/* Headline card */}
            <div className="bg-[#0F1E33] text-[#F5F8FB] rounded-2xl p-7">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#93C5FD]">
                    Now exploring
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">
                    {selectedName ? `${selectedName}, Maine` : "Loading…"}
                  </h3>
                </div>
                {yoy != null && (
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-full ${
                        yoyPositive
                          ? "bg-[#93C5FD]/20 text-[#93C5FD]"
                          : "bg-red-500/20 text-red-300"
                      }`}
                    >
                      <TrendingUp size={14} />
                      {yoyPositive ? "+" : ""}
                      {yoy.toFixed(1)}% YoY
                    </span>
                  </div>
                )}
              </div>
              {snapshot?.blurb && (
                <p className="text-sm opacity-85 mt-4 leading-relaxed max-w-2xl">
                  {snapshot.blurb}
                </p>
              )}
            </div>

            {/* Stat grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                icon={DollarSign}
                label="Median sale price"
                value={
                  snapshotLoading
                    ? "…"
                    : formatCurrency(snapshot?.medianPrice)
                }
              />
              <StatCard
                icon={Clock}
                label="Avg days on market"
                value={
                  snapshotLoading
                    ? "…"
                    : snapshot?.daysOnMarket != null
                    ? `${snapshot.daysOnMarket} days`
                    : "—"
                }
              />
              <StatCard
                icon={BarChart3}
                label="Price per sq ft"
                value={
                  snapshotLoading
                    ? "…"
                    : snapshot?.pricePerSqft != null
                    ? `$${Math.round(snapshot.pricePerSqft)}`
                    : "—"
                }
              />
              <StatCard
                icon={HomeIcon}
                label="Active listings"
                value={
                  snapshotLoading
                    ? "…"
                    : snapshot?.newListingsCount ?? "—"
                }
              />
            </div>

            {/* CTAs */}
            <div className="bg-card border border-border rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-foreground/80">
                Want a custom report for{" "}
                <strong className="text-foreground">{selectedName || "your town"}</strong>{" "}
                — including comparable sales near your address?
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`${KW_SEARCH_URL}?searchTerm=${encodeURIComponent(
                    selectedName
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`view-listings-${selectedSlug}`}
                >
                  <Button
                    variant="outline"
                    className="rounded-full h-10 border-border"
                  >
                    View listings <ExternalLink className="ml-2" size={14} />
                  </Button>
                </a>
                <Link to="/booking" data-testid={`book-call-${selectedSlug}`}>
                  <Button className="rounded-full bg-primary hover:bg-primary/90 h-10">
                    Talk to Tammy <ArrowRight className="ml-2" size={14} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <p className="text-foreground/65 mt-12 text-sm">
          Brokered by{" "}
          <strong className="text-foreground">Keller Williams Maine</strong>.
          If your town isn't listed here, ask — Tammy's network reaches across
          the state and to trusted partners well beyond it.
        </p>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <Icon size={16} className="text-primary" strokeWidth={1.7} />
      <p className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 mt-3">
        {label}
      </p>
      <p className="font-serif text-2xl md:text-[26px] mt-1 leading-tight text-foreground">
        {value}
      </p>
    </div>
  );
}
